/*
 * Maps all the values of a Map.
 */
export function mapValues<A, B, C>(
  map: ReadonlyMap<A, B>,
  fn: (value: B, key: A) => C,
): Map<A, C> {
  const result = new Map<A, C>()

  map.forEach((value, key) => {
    result.set(key, fn(value, key))
  })

  return result
}

/**
 * Determines whether the specified callback function returns true for any key/value of the Map.
 */
export function some<A, B>(
  map: ReadonlyMap<A, B>,
  fn: (value: B, key: A) => boolean,
): boolean {
  for (const [key, value] of map) {
    if (fn(value, key)) return true
  }
  return false
}

/**
 * Groups elements from an iterable into a Map based on a callback function.
 *
 * Note: This is on track to become an ES standard but is still very new:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map/groupBy
 * Replace usages with the standard method when we bump tsc's ES target.
 */
export function groupBy<K, T>(
  items: Iterable<T>,
  keySelector: (value: T, index: number) => K,
): Map<K, T[]> {
  const map = new Map<K, T[]>()

  let i = 0
  for (const value of items) {
    const key = keySelector(value, i++)
    const list = map.get(key)
    list ? list.push(value) : map.set(key, [value])
  }

  return map
}

/**
 * Creates a Map from an array of items, using a key function to determine the key.
 * Optionally, a value function can be provided to determine the value for each key.
 */
export function keyBy<T, K>(array: T[], keyFn: (item: T) => K): Map<K, T>
export function keyBy<T, K, V>(
  array: T[],
  keyFn: (item: T) => K,
  valueFn: (item: T) => V,
): Map<K, V>
export function keyBy<T, K, V>(
  array: T[],
  keyFn: (item: T) => K,
  valueFn?: (item: T) => V,
): Map<K, T | V> {
  return array.reduce((map, currentItem) => {
    const key = keyFn(currentItem)
    const value = valueFn ? valueFn(currentItem) : currentItem
    map.set(key, value)
    return map
  }, new Map<K, T | V>())
}
